import { Injectable } from '@angular/core';
import { SharedService } from './shared.service';

declare const smartech: any;

const DEFAULT_SCRIPT_URL = 'https://cdnt.netcoresmartech.com/smartechclient.js';

/**
 * Netcore (Smartech) web tracking for the whole staticweb app — counterpart of the
 * portal's NetCoreService bootstrap. AppComponent calls init() once at start-up, which
 * injects the tracker and runs Netcore's install snippet (create / register / identify('')).
 *
 * The script is injected rather than put in index.html so the keys and the on/off switch
 * live in `netcoreConfig` (configurations.json) per environment. Every call is guarded:
 * ad/tracker blockers stop the script loading, and analytics must never break the app.
 */
@Injectable({
  providedIn: 'root'
})
export class NetcoreService {
  private ready: Promise<boolean> | null = null;

  constructor(private sharedSvc: SharedService) { }

  private get config(): any {
    return (this.sharedSvc.configDetails && this.sharedSvc.configDetails.netcoreConfig) || {};
  }

  get isActive(): boolean {
    return this.config.isActive === true && !!this.config.createKey && !!this.config.registerKey;
  }

  /** Loads and bootstraps the tracker when netcoreConfig is active; safe to call more than once */
  init(): Promise<boolean> {
    return this.isActive ? this.load() : Promise.resolve(false);
  }

  private load(): Promise<boolean> {
    if (!this.ready) {
      this.ready = new Promise<boolean>(resolve => {
        const bootstrap = () => {
          // Same guard name as the portal, in case both ever share a page
          const w = window as any;
          if (!w.__netcoreBootstrapped) {
            w.__netcoreBootstrapped = true;
            this.callSmartech('create', this.config.createKey, this.config.dataCenter || 'tin');
            this.callSmartech('register', this.config.registerKey);
            this.callSmartech('identify', '');
          }
          resolve(typeof smartech === 'function');
        };
        if (typeof smartech === 'function') {
          bootstrap();
          return;
        }
        const script = document.createElement('script');
        script.src = this.config.scriptUrl || DEFAULT_SCRIPT_URL;
        script.async = true;
        script.onload = bootstrap;
        script.onerror = () => {
          // Blocked or unreachable — allow a later retry instead of caching the failure
          this.ready = null;
          resolve(false);
        };
        document.head.appendChild(script);
      });
    }
    return this.ready;
  }

  private callSmartech(...args: any[]) {
    try {
      if (typeof smartech === 'function') {
        smartech(...args);
      }
    } catch (_e) {
      // tracker blocked/unavailable — silently skip the analytics call
    }
  }
}
